import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { AdmPlan } from "@custom-types/base-types";
import { BaseProduct } from "@utils/product/product-utils-types";
import { AllSphereXg2026Bundles } from "@utils/product/product-types";
import { isHoloBuilderLegacyPlanIdentifier } from "@utils/type-guards";
import {
  grantsAnyBundle,
  grantsFeature,
  HB_TO_SXG_MIGRATED_IDENTIFIER,
  isSubscriptionPlan,
  isTokenPlan,
} from "@utils/plan-utils";

/** Feature identifier that opens the Sphere XG dashboard instead of the HoloBuilder one */
const SPHERE_DASHBOARD_IDENTIFIER: FaroCoreApiTypes.FeatureIdentifier =
  "sphere-dashboard";

/** The applications a plan can grant access to */
export const PLAN_APPS = ["holoBuilder", "sphereXg"] as const;
export type PlanApp = (typeof PLAN_APPS)[number];

/** Whether the value is one of the applications a plan can grant access to */
export function isPlanApp(value: string): value is PlanApp {
  return (PLAN_APPS as readonly string[]).includes(value);
}

/** Display name of every application */
export const PLAN_APP_NAMES: Record<PlanApp, string> = {
  holoBuilder: "HoloBuilder",
  sphereXg: "Sphere XG",
};

/** The application settings that are attached to the subscription of a plan */
export interface PlanAppSettings {
  /** The application that opens the 360° images of the plan */
  defaultViewerApp: PlanApp;

  /** The dashboard that is opened after the members of the plan log in */
  defaultDashboardApp: PlanApp;
}

/** The applications a plan opens unless the user picks different ones */
export const DEFAULT_PLAN_APP_SETTINGS: PlanAppSettings = {
  defaultViewerApp: "holoBuilder",
  defaultDashboardApp: "sphereXg",
};

/** The applications the legacy HoloBuilder plans open, as they predate Sphere XG on both sides */
const LEGACY_PLAN_APP_SETTINGS: PlanAppSettings = {
  defaultViewerApp: "holoBuilder",
  defaultDashboardApp: "holoBuilder",
};

/** The applications of the plans that never open in HoloBuilder at all */
const SPHERE_XG_ONLY_PLAN_APP_SETTINGS: PlanAppSettings = {
  defaultViewerApp: "sphereXg",
  defaultDashboardApp: "sphereXg",
};

/**
 * @returns whether a plan of the given product lets the user pick its applications. The settings
 * choose between HoloBuilder and Sphere XG, so a product that never opens in HoloBuilder, such as
 * a Point Cloud plan without 360°, has no choice to offer.
 * @param product the product the plan is created from
 */
export function offersPlanAppChoice(product: BaseProduct): boolean {
  return product.apps.includes("holoBuilder");
}

/**
 * @returns the applications a plan of the given product opens unless the user picks different ones
 * @param product the product the plan is created from
 */
export function defaultPlanAppSettingsOf(
  product: BaseProduct
): PlanAppSettings {
  return isHoloBuilderLegacyPlanIdentifier(product.identifier)
    ? LEGACY_PLAN_APP_SETTINGS
    : DEFAULT_PLAN_APP_SETTINGS;
}

/**
 * @returns the features that apply the application settings to the created subscription
 * @param settings the application settings the user picked
 */
export function planAppFeatures(
  settings: PlanAppSettings
): FaroCoreApiTypes.FeatureIdentifier[] {
  const features: FaroCoreApiTypes.FeatureIdentifier[] = [];

  if (settings.defaultViewerApp === "sphereXg") {
    features.push(HB_TO_SXG_MIGRATED_IDENTIFIER);
  }
  if (settings.defaultDashboardApp === "sphereXg") {
    features.push(SPHERE_DASHBOARD_IDENTIFIER);
  }

  return features;
}

/**
 * The identifiers that pick the applications of a plan. Each exists both as a feature and as a
 * bundle of the same name that only grants that feature.
 */
const PLAN_APP_IDENTIFIERS: readonly string[] = [
  HB_TO_SXG_MIGRATED_IDENTIFIER,
  SPHERE_DASHBOARD_IDENTIFIER,
];

/**
 * Whether the plan is one of the subscriptions that only ever open in Sphere XG: the 2026 Scan
 * plans, and the 2025 plans sold with the Point Cloud addon but without the 360° one. They are
 * recognized by their bundles, as they carry no application feature of their own.
 */
function isSphereXgOnlyPlan(plan: AdmPlan): boolean {
  const is2026ScanPlan = grantsAnyBundle(plan, AllSphereXg2026Bundles);
  const isPointCloudWithout360 =
    grantsAnyBundle(plan, ["sphere-xg-addon-point-cloud"]) &&
    !grantsAnyBundle(plan, ["sphere-xg-addon-360"]);

  return is2026ScanPlan || isPointCloudWithout360;
}

/**
 * @returns the applications an existing plan opens, or null for plans that open none of them,
 * such as add-ons and credits
 * @param plan the plan to read the granted features of
 */
export function planAppSettingsOf(plan: AdmPlan): PlanAppSettings | null {
  if (!isSubscriptionPlan(plan) || isTokenPlan(plan)) {
    return null;
  }

  if (isSphereXgOnlyPlan(plan)) {
    return SPHERE_XG_ONLY_PLAN_APP_SETTINGS;
  }

  return {
    defaultViewerApp: grantsFeature(plan, HB_TO_SXG_MIGRATED_IDENTIFIER)
      ? "sphereXg"
      : "holoBuilder",
    defaultDashboardApp: grantsFeature(plan, SPHERE_DASHBOARD_IDENTIFIER)
      ? "sphereXg"
      : "holoBuilder",
  };
}

/** Which of the applications of an existing plan can be changed */
export interface PlanAppEditability {
  /** Whether the viewer application can be changed */
  canChangeViewer: boolean;

  /** Whether the dashboard application can be changed */
  canChangeDashboard: boolean;
}

/**
 * @returns which of the applications of an existing plan can be changed. Nothing can be changed
 * on a plan the backend no longer updates, i.e. a deactivated or ended one, nor on a plan that
 * only ever opens in Sphere XG. An application that a bundle such as the core product includes
 * is fixed as well, as that bundle cannot be dropped without taking away the plan itself.
 * @param plan the plan to change the applications of
 * @param now the moment to check the end date of the plan against
 */
export function planAppEditabilityOf(
  plan: AdmPlan,
  now: Date = new Date()
): PlanAppEditability {
  const hasEnded = !!plan.endDate && new Date(plan.endDate) <= now;
  const canBeUpdated =
    plan.status.identifier !== "deactivated" &&
    !hasEnded &&
    planAppSettingsOf(plan) !== null &&
    !isSphereXgOnlyPlan(plan);

  return {
    canChangeViewer:
      canBeUpdated &&
      !grantsFeatureThroughOtherBundle(plan, HB_TO_SXG_MIGRATED_IDENTIFIER),
    canChangeDashboard:
      canBeUpdated &&
      !grantsFeatureThroughOtherBundle(plan, SPHERE_DASHBOARD_IDENTIFIER),
  };
}

/** The features and bundles to update a plan with, as identifiers */
export interface PlanAppUpdate {
  /** Every feature the plan grants on its own */
  features: string[];

  /** Every bundle of the plan, only set when the bundles change */
  featureBundles?: string[];
}

/**
 * @returns the features and bundles of the plan once it opens the given applications. The
 * application features are granted directly, and the bundles of the same name that products
 * attach are dropped, so they no longer pick an application. The backend replaces the whole
 * lists, so every other feature and bundle is kept as it is.
 * @param plan the plan to change the applications of
 * @param settings the applications the plan should open
 */
export function planAppUpdateOf(
  plan: AdmPlan,
  settings: PlanAppSettings
): PlanAppUpdate {
  const otherFeatures = plan.features
    .map(({ identifier }) => identifier)
    .filter((identifier) => !PLAN_APP_IDENTIFIERS.includes(identifier));
  const bundles = plan.featureBundles.map(({ identifier }) => identifier);
  const otherBundles = bundles.filter(
    (identifier) => !PLAN_APP_IDENTIFIERS.includes(identifier)
  );

  return {
    features: [...otherFeatures, ...planAppFeatures(settings)],
    ...(otherBundles.length !== bundles.length && {
      featureBundles: otherBundles,
    }),
  };
}

/**
 * Whether a bundle of the plan includes the feature, other than the bundle of the same name
 * that only exists to grant it
 */
function grantsFeatureThroughOtherBundle(
  plan: AdmPlan,
  feature: string
): boolean {
  return plan.featureBundles.some(
    ({ identifier, features }) =>
      !PLAN_APP_IDENTIFIERS.includes(identifier) &&
      features.some((bundled) => bundled.identifier === feature)
  );
}
